import { Chip, ChipProps } from "@mui/material";
import React from "react";
import {
  handleLinearTagClick,
  handleLinearTagDoubleClick,
  isLinearIssue,
} from "routes/Staff/WorkflowConfig/linearTagHelpers";
import { getLinearStatusColor } from "util/linearStatusColors";

interface LinearIssueStatusMap {
  [identifier: string]: { status: string; url: string };
}

interface LinearTagChipProps {
  tag: string;
  linearStatusMap: LinearIssueStatusMap;
  onNormalClick?: () => void;
  sx?: ChipProps["sx"];
  size?: "small" | "medium";
  onDelete?: () => void;
  disabled?: boolean;
}

/**
 * Reusable chip component for displaying tags with Linear integration.
 * Automatically detects Linear tags (ENG-XXX pattern) and applies appropriate styling/handlers.
 * Works seamlessly with mixed collections of Linear and regular tags.
 */
export function LinearTagChip({
  tag,
  linearStatusMap,
  onNormalClick,
  sx,
  size = "small",
  onDelete,
  disabled = false,
}: LinearTagChipProps) {
  const isLinear = isLinearIssue(tag);
  const issueInfo = isLinear ? linearStatusMap[tag.toUpperCase()] : null;
  const backgroundColor = issueInfo
    ? getLinearStatusColor(issueInfo.status)
    : undefined;

  return (
    <Chip
      label={tag}
      size={size}
      onDelete={onDelete}
      disabled={disabled}
      onClick={(e) =>
        handleLinearTagClick(e, tag, linearStatusMap, onNormalClick)
      }
      onDoubleClick={(e) =>
        handleLinearTagDoubleClick(e, tag, linearStatusMap)
      }
      sx={{
        height: size === "small" ? 20 : undefined,
        fontSize: size === "small" ? "0.7rem" : undefined,
        flexShrink: 0,
        maxWidth: size === "small" ? 80 : undefined,
        backgroundColor,
        cursor:
          isLinear && issueInfo && !disabled ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          padding: size === "small" ? "0 8px" : undefined,
          display: "flex",
          alignItems: "center",
          lineHeight: "normal",
        },
        ...sx,
      }}
    />
  );
}
